import {
  addDays,
  addMonths,
  firstDayOfWeek,
  monthGrid,
  monthOf,
  weekdayNames,
} from "@breenova/domain";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

import { cn } from "@breenova/ui";

export interface DateFieldProps {
  /** `YYYY-MM-DD`, or empty for no date. */
  value: string;
  onChange: (value: string) => void;
  /** The accessible name. The visible label above the field is separate. */
  label: string;
  id?: string;
  placeholder?: string;
  /** Offers a "Clear" action, for a date the record does not have to carry. */
  clearable?: boolean;
  /** Days a breeder should be able to see marked — an expected hatch, a ringing day. */
  marked?: readonly string[];
  disabled?: boolean;
  className?: string;
}

/** Today, as a plain calendar date in the reader's own zone. */
function today(): string {
  const now = new Date();

  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
}

function formatLong(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);

  if (!year || !month || !day) {
    return iso;
  }

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatMonth(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);

  return new Date(Date.UTC(year!, monthNumber! - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * A date field with a calendar the app draws itself.
 *
 * Lives here rather than in `packages/ui` for the same reason `RingNumberField` does:
 * it is built out of the design system but knows this app's data — plain `YYYY-MM-DD`
 * dates as the API sends them — and the design system has no business depending on the
 * domain package to find that out.
 *
 * A native `input[type=date]` gets the operating system's picker — grey system chrome
 * that ignores every token in this design — and a placeholder in the browser's locale
 * rather than the breeder's ("åååå-mm-dd" on a Swedish Chrome). This is the same trade
 * the Picker makes: give up the platform control, and take on its keyboard contract.
 *
 * Anchored under the field rather than opened as a sheet, because two of these live
 * inside a bottom sheet already and a dialog inside a dialog is a stacking problem.
 *
 * Dates are plain `YYYY-MM-DD` throughout and every calculation runs in UTC. A hatch
 * date is a day, not an instant, and local getters are how a picker ends up storing
 * the day before the one that was tapped.
 */
export function DateField({
  value,
  onChange,
  label,
  id,
  placeholder = "Choose a date",
  clearable = true,
  marked,
  disabled = false,
  className,
}: DateFieldProps) {
  const generatedId = useId();
  const gridId = useId();
  const triggerId = id ?? generatedId;

  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; top: number; width: number }>();

  // The day the arrow keys are sitting on. Separate from the chosen value: moving
  // around a month should not commit a date on every keystroke.
  const [cursor, setCursor] = useState(() => value || today());
  const [month, setMonth] = useState(() => monthOf(value || today()));

  const weekStart = useMemo(() => firstDayOfWeek(), []);
  const weekdays = useMemo(() => weekdayNames(weekStart), [weekStart]);
  const grid = useMemo(() => monthGrid(month, weekStart), [month, weekStart]);
  const markedDays = useMemo(() => new Set(marked ?? []), [marked]);

  const now = today();

  // Reopening on the chosen date rather than wherever it was last left.
  useEffect(() => {
    if (open) {
      const start = value || today();

      setCursor(start);
      setMonth(monthOf(start));
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: PointerEvent): void {
      if (!wrapper.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  /*
   * The panel is positioned against the viewport rather than against the field.
   *
   * Two of these sit inside a bottom sheet, whose body scrolls — and an absolutely
   * positioned panel is clipped by any scrolling ancestor. A six-week grid is taller
   * than the sheet's visible body, so the last fortnight simply disappeared. `fixed`
   * ignores the overflow, and flipping above the field when there is no room below is
   * what keeps it on screen at the bottom of a form.
   *
   * Laid out before paint, so the panel never appears in the wrong place first.
   */
  useLayoutEffect(() => {
    if (!open) {
      setAnchor(undefined);
      return;
    }

    function place(): void {
      const field = trigger.current?.getBoundingClientRect();
      const box = panel.current?.getBoundingClientRect();

      if (!field || !box) {
        return;
      }

      const gap = 8;
      const margin = 8;
      const below = field.bottom + gap;
      const fitsBelow = below + box.height <= window.innerHeight - margin;

      setAnchor({
        left: field.left,
        top: fitsBelow ? below : Math.max(margin, field.top - gap - box.height),
        width: field.width,
      });
    }

    place();

    // Capture, so an ancestor scrolling — the sheet's own body — repositions it too.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);

    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, month]);

  function moveTo(next: string): void {
    setCursor(next);
    setMonth(monthOf(next));
  }

  function choose(iso: string): void {
    onChange(iso);
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (disabled) {
      return;
    }

    if (!open) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        event.preventDefault();
        setOpen(true);
      }

      return;
    }

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        moveTo(addDays(cursor, -1));
        break;
      case "ArrowRight":
        event.preventDefault();
        moveTo(addDays(cursor, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        moveTo(addDays(cursor, -7));
        break;
      case "ArrowDown":
        event.preventDefault();
        moveTo(addDays(cursor, 7));
        break;
      case "PageUp":
        event.preventDefault();
        moveTo(addDays(cursor, -28));
        break;
      case "PageDown":
        event.preventDefault();
        moveTo(addDays(cursor, 28));
        break;
      case "Home":
        event.preventDefault();
        moveTo(`${month}-01`);
        break;
      case "End": {
        event.preventDefault();
        const last = grid.filter((cell) => cell.inMonth).at(-1);
        if (last) {
          moveTo(last.iso);
        }
        break;
      }
      case "Enter":
      case " ":
        event.preventDefault();
        choose(cursor);
        break;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  }

  return (
    <div ref={wrapper} className={cn("relative", className)}>
      <button
        ref={trigger}
        type="button"
        id={triggerId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-13.5 w-full items-center justify-between gap-md rounded-control px-5",
          "bg-surface-container-lowest text-left text-field font-normal shadow-card",
          value ? "text-on-surface" : "text-on-surface-variant",
          "outline-none transition-shadow",
          "focus-visible:shadow-[inset_0_0_0_2.75px_var(--color-action)]",
          "disabled:cursor-not-allowed disabled:opacity-45",
        )}
      >
        <span className="truncate" data-numeric>
          {value ? formatLong(value) : placeholder}
        </span>
        <CalendarDays aria-hidden className="size-4.5 shrink-0 text-on-surface-variant" />
      </button>

      {open && !disabled ? (
        <div
          ref={panel}
          role="dialog"
          aria-label={label}
          style={
            anchor
              ? { left: anchor.left, top: anchor.top, width: anchor.width }
              : // Measured on the first layout pass; hidden until then so it is never
                // painted at the top-left corner on the way to its real position.
                { visibility: "hidden" }
          }
          className={cn(
            "fixed z-30",
            "rounded-lg bg-surface-container-lowest p-md shadow-raised",
          )}
        >
          <div className="mb-sm flex items-center justify-between gap-sm">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setMonth(addMonths(month, -1))}
              className="flex size-touch shrink-0 items-center justify-center rounded-full text-on-surface transition-colors hover:bg-surface-container"
            >
              <ChevronLeft className="size-5" />
            </button>

            <span aria-live="polite" className="truncate text-label text-on-surface">
              {formatMonth(month)}
            </span>

            <button
              type="button"
              aria-label="Next month"
              onClick={() => setMonth(addMonths(month, 1))}
              className="flex size-touch shrink-0 items-center justify-center rounded-full text-on-surface transition-colors hover:bg-surface-container"
            >
              <ChevronRight className="size-5" />
            </button>
          </div>

          <div aria-hidden className="grid grid-cols-7 gap-hair">
            {weekdays.map((name, index) => (
              <span
                key={`${name}-${index}`}
                className="flex h-6 items-center justify-center text-tile-label uppercase text-on-surface-variant"
              >
                {name}
              </span>
            ))}
          </div>

          {/*
           * A roving grid: one tab stop, and `aria-activedescendant` says which day the
           * arrows are on. Thirty-two tab stops inside a form field is not navigation,
           * it is a trap.
           */}
          <div
            id={gridId}
            role="grid"
            tabIndex={0}
            aria-label={label}
            aria-activedescendant={`${gridId}-${cursor}`}
            onKeyDown={handleKeyDown}
            className="mt-hair grid grid-cols-7 gap-hair outline-none"
            ref={(node) => node?.focus()}
          >
            {grid.map((cell) => {
              const isChosen = cell.iso === value;
              const isToday = cell.iso === now;
              const isCursor = cell.iso === cursor;

              return (
                <button
                  key={cell.iso}
                  id={`${gridId}-${cell.iso}`}
                  type="button"
                  role="gridcell"
                  tabIndex={-1}
                  aria-selected={isChosen}
                  aria-current={isToday ? "date" : undefined}
                  onClick={() => choose(cell.iso)}
                  className={cn(
                    "relative flex h-10 items-center justify-center rounded-full",
                    "text-data transition-colors",
                    isChosen
                      ? "bg-secondary-container text-on-secondary-container"
                      : isCursor
                        ? "bg-surface-container text-on-surface"
                        : cell.inMonth
                          ? "text-on-surface hover:bg-surface-container-low"
                          : // Present but recessive: a grid that hides its neighbouring
                            // days makes the first and last rows look broken.
                            "text-on-surface-variant/50 hover:bg-surface-container-low",
                    isToday && !isChosen ? "shadow-[inset_0_0_0_2px_var(--color-accent-mark)]" : "",
                  )}
                >
                  <span data-numeric>{cell.day}</span>

                  {markedDays.has(cell.iso) && !isChosen ? (
                    <span
                      aria-hidden
                      className="absolute bottom-hair size-1 rounded-full bg-accent-mark"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="mt-sm flex items-center justify-between gap-sm">
            <button
              type="button"
              onClick={() => choose(now)}
              className="rounded-control px-md py-sm text-label-caps uppercase text-primary transition-colors hover:bg-surface-container-low"
            >
              Today
            </button>

            {clearable && value ? (
              <button
                type="button"
                onClick={() => choose("")}
                className="rounded-control px-md py-sm text-label-caps uppercase text-on-surface-variant transition-colors hover:bg-surface-container-low"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
